import { FormEvent, useEffect, useMemo, useState } from 'react';
import { GARMENT_GROUPS, type GarmentGroup, type MeasurementPoint } from '@rosita/shared';
import { api, extractError } from '../../../lib/api';
import { useToast } from '../../../components/admin/Toast';
import {
  GARMENT_GROUP_LABELS,
  dangerBtnText,
  inputClass,
  primaryBtn,
  secondaryBtn,
  slugify,
} from './shared';

const ENDPOINT = '/admin/measurement-points';

interface Draft {
  name: string;
  slug: string;
  garmentGroups: GarmentGroup[];
  sortOrder: number;
  isActive: boolean;
}

function toggleGroup(groups: GarmentGroup[], group: GarmentGroup): GarmentGroup[] {
  return groups.includes(group) ? groups.filter((g) => g !== group) : [...groups, group];
}

function GroupCheckboxes({
  value,
  onChange,
}: {
  value: GarmentGroup[];
  onChange: (next: GarmentGroup[]) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {GARMENT_GROUPS.map((g) => (
        <label key={g} className="flex items-center gap-1 text-sm">
          <input
            type="checkbox"
            checked={value.includes(g)}
            onChange={() => onChange(toggleGroup(value, g))}
          />
          {GARMENT_GROUP_LABELS[g]}
        </label>
      ))}
    </div>
  );
}

// Measurement points ("Peito", "Comprimento da manga", …) with the garment
// groups they apply to. Same CRUD shape as NamedSlugTab plus the group
// checkboxes, which that component has no slot for.
export default function MeasurementPointsTab(): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<MeasurementPoint[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [groups, setGroups] = useState<GarmentGroup[]>([]);
  const [sortOrder, setSortOrder] = useState<number | null>(null);
  const [sortOrderManuallyEdited, setSortOrderManuallyEdited] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const ensureUniqueSlug = (base: string): string => {
    if (!base) return '';
    const taken = new Set(items.map((i) => i.slug));
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base}-${n}`)) n += 1;
    return `${base}-${n}`;
  };

  const nextSortOrder = useMemo(
    () => items.reduce((max, i) => Math.max(max, i.sortOrder), 0) + 10,
    [items]
  );
  const effectiveSortOrder =
    sortOrderManuallyEdited && sortOrder !== null ? sortOrder : nextSortOrder;
  const slugCollision = slug !== '' && items.some((i) => i.slug === slug);

  const load = async (): Promise<void> => {
    const response = await api.get<MeasurementPoint[]>(ENDPOINT);
    setItems(response.data);
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    try {
      await api.post(ENDPOINT, {
        name,
        slug,
        garmentGroups: groups,
        sortOrder: effectiveSortOrder,
        isActive: true,
      });
      setName('');
      setSlug('');
      setSlugManuallyEdited(false);
      setGroups([]);
      setSortOrder(null);
      setSortOrderManuallyEdited(false);
      toast.success('Medida criada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar medida'));
    }
  };

  const startEdit = (item: MeasurementPoint): void => {
    setEditingId(item.id);
    setDraft({
      name: item.name,
      slug: item.slug,
      garmentGroups: item.garmentGroups,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
  };

  const saveEdit = async (): Promise<void> => {
    if (!editingId || !draft) return;
    try {
      await api.put(`${ENDPOINT}/${editingId}`, draft);
      setEditingId(null);
      setDraft(null);
      toast.success('Medida atualizada.');
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao atualizar medida'));
    }
  };

  const remove = async (item: MeasurementPoint): Promise<void> => {
    if (
      !window.confirm(
        `Apagar "${item.name}"? Os valores desta medida desaparecem de todos os produtos.`
      )
    )
      return;
    try {
      const res = await api.delete<{ ok: boolean; productsAffected: number }>(
        `${ENDPOINT}/${item.id}`
      );
      const n = res.data.productsAffected;
      toast.success(
        n > 0 ? `Medida eliminada (removida de ${n} produto${n === 1 ? '' : 's'}).` : 'Medida eliminada.'
      );
      await load();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao apagar medida'));
    }
  };

  return (
    <section>
      <p className="mb-4 text-sm text-admin-muted">
        Pontos de medida da peça (em cm) que podes preencher por tamanho em cada produto. Os
        grupos servem para aplicar um conjunto de medidas de uma vez no formulário do produto.
      </p>
      <form
        onSubmit={(e) => {
          void handleCreate(e);
        }}
        className="mb-6 grid grid-cols-1 gap-2 rounded-md bg-admin-surface-alt p-3 md:grid-cols-[1fr_1fr_100px_auto]"
      >
        <input
          placeholder="Nome (ex.: Peito)"
          value={name}
          onChange={(e) => {
            const v = e.target.value;
            setName(v);
            if (!slugManuallyEdited) setSlug(ensureUniqueSlug(slugify(v)));
          }}
          required
          className={inputClass}
        />
        <input
          placeholder="slug"
          value={slug}
          onChange={(e) => {
            const v = e.target.value;
            setSlug(v);
            setSlugManuallyEdited(v !== '');
          }}
          required
          className={inputClass}
        />
        <input
          type="number"
          value={effectiveSortOrder}
          onChange={(e) => {
            setSortOrder(Number(e.target.value));
            setSortOrderManuallyEdited(true);
          }}
          className={inputClass}
          placeholder="Ordem"
        />
        <button type="submit" disabled={slugCollision} className={primaryBtn}>
          Criar
        </button>
        <div className="md:col-span-4">
          <span className="mr-3 text-sm text-admin-text-soft">Aplica-se a:</span>
          <GroupCheckboxes value={groups} onChange={setGroups} />
        </div>
      </form>
      {slugCollision ? (
        <p className="mb-4 text-sm text-admin-danger">Já existe uma medida com o slug "{slug}".</p>
      ) : null}

      <ul className="overflow-hidden rounded-lg border border-admin-border bg-admin-surface">
        {items.length === 0 ? (
          <li className="p-4 text-sm text-admin-muted">Sem medidas.</li>
        ) : null}
        {items.map((item) => {
          const isEditing = editingId === item.id;
          return (
            <li
              key={item.id}
              className="flex flex-wrap items-center gap-3 border-b border-admin-border p-3 last:border-b-0"
            >
              {isEditing && draft ? (
                <>
                  <input
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    value={draft.slug}
                    onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                    className={inputClass}
                  />
                  <input
                    type="number"
                    value={draft.sortOrder}
                    onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) })}
                    className={`${inputClass} w-24`}
                  />
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                    Ativa
                  </label>
                  <div className="basis-full">
                    <GroupCheckboxes
                      value={draft.garmentGroups}
                      onChange={(next) => setDraft({ ...draft, garmentGroups: next })}
                    />
                  </div>
                  <button type="button" onClick={() => void saveEdit()} className={primaryBtn}>
                    Guardar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(null);
                      setDraft(null);
                    }}
                    className={secondaryBtn}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1">
                    {item.name} <small className="text-admin-muted">({item.slug})</small>
                    {!item.isActive ? (
                      <span className="ml-2 rounded bg-admin-surface-alt px-2 py-0.5 text-xs text-admin-muted">
                        inativa
                      </span>
                    ) : null}
                    <span className="mt-1 flex flex-wrap gap-1">
                      {item.garmentGroups.length === 0 ? (
                        <span className="text-xs text-admin-muted">sem grupo</span>
                      ) : (
                        item.garmentGroups.map((g) => (
                          <span
                            key={g}
                            className="rounded-full border border-admin-border px-2 py-0.5 text-xs text-admin-text-soft"
                          >
                            {GARMENT_GROUP_LABELS[g]}
                          </span>
                        ))
                      )}
                    </span>
                  </span>
                  <span className="text-xs text-admin-muted">ordem {item.sortOrder}</span>
                  <button type="button" onClick={() => startEdit(item)} className={secondaryBtn}>
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void remove(item);
                    }}
                    className={dangerBtnText}
                  >
                    Apagar
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
